import { useState } from 'react'
import { toast } from 'sonner'
import { ExternalLink } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useGscSitemaps, useGscInspect } from '@/hooks/useApi'
import type { GscSitemap } from '@/api/client'
import { cn } from '@/lib/utils'

/**
 * Sitemap status and ad-hoc URL inspection for one dossier's GSC property.
 *
 * Both used to sit behind a global property picker on the SEO page, where you
 * first had to pick a property that the dossier already knows. Scoped to the
 * dossier, the picker disappears and the two tools answer questions about
 * *this* site.
 *
 * Deliberately not merged with the Indexeerbaarheid tab: that renders a stored
 * report from the nightly run, while this is a live lookup against Google.
 */

const VERDICT_LABELS: Record<string, string> = {
  PASS: 'Geïndexeerd',
  FAIL: 'Niet geïndexeerd',
  NEUTRAL: 'Onbekend',
  VERDICT_UNSPECIFIED: 'Onbekend',
}

const VERDICT_CLASSES: Record<string, string> = {
  PASS: 'bg-green-600 text-white',
  FAIL: 'bg-red-600 text-white',
  NEUTRAL: 'bg-gray-400 text-white',
  VERDICT_UNSPECIFIED: 'bg-gray-400 text-white',
}

function rateIndexRatio(ratio: number): {
  label: string
  barColor: string
  textColor: string
  advice: string
} {
  if (ratio >= 0.9)
    return {
      label: 'Uitstekend',
      barColor: 'bg-green-500',
      textColor: 'text-green-600 dark:text-green-300',
      advice: "Bijna alle ingediende pagina's zijn geïndexeerd.",
    }
  if (ratio >= 0.7)
    return {
      label: 'Goed',
      barColor: 'bg-green-400',
      textColor: 'text-green-500',
      advice: "Meeste pagina's zijn geïndexeerd.",
    }
  if (ratio >= 0.5)
    return {
      label: 'Matig',
      barColor: 'bg-orange-400',
      textColor: 'text-orange-500',
      advice: 'Controleer op noindex-tags, duplicate content en crawl-problemen.',
    }
  return {
    label: 'Probleem',
    barColor: 'bg-red-500',
    textColor: 'text-red-600 dark:text-red-300',
    advice: 'Meer dan de helft wordt niet geïndexeerd. Check crawl-fouten in GSC.',
  }
}

function SitemapCard({ sitemap }: { sitemap: GscSitemap }) {
  const webUrls = sitemap.contents.find((c) => c.type === 'WEB')
  const ratio = webUrls && webUrls.submitted > 0 ? webUrls.indexed / webUrls.submitted : null
  const rating = ratio !== null ? rateIndexRatio(ratio) : null

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-2">
        <a
          href={sitemap.path}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 truncate text-sm font-medium text-primary hover:underline"
        >
          {sitemap.path} <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
        <div className="flex shrink-0 gap-1">
          {rating && (
            <Badge
              className={cn(
                'text-xs text-white',
                ratio! >= 0.7 ? 'bg-green-600' : ratio! >= 0.5 ? 'bg-orange-500' : 'bg-red-600',
              )}
            >
              {rating.label}
            </Badge>
          )}
          {sitemap.errors > 0 && (
            <Badge className="bg-red-600 text-xs text-white">{sitemap.errors} fouten</Badge>
          )}
          {sitemap.warnings > 0 && (
            <Badge className="bg-orange-500 text-xs text-white">{sitemap.warnings} waarsch.</Badge>
          )}
          {sitemap.is_pending && (
            <Badge variant="outline" className="text-xs">
              In behandeling
            </Badge>
          )}
        </div>
      </div>

      {webUrls && webUrls.submitted > 0 && rating && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {webUrls.indexed.toLocaleString('nl')} van {webUrls.submitted.toLocaleString('nl')}{' '}
              URL's geïndexeerd
            </span>
            <span className={cn('font-semibold', rating.textColor)}>
              {Math.round(ratio! * 100)}%
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full transition-all', rating.barColor)}
              style={{ width: `${Math.round(ratio! * 100)}%` }}
            />
          </div>
          <p className={cn('text-xs', rating.textColor)}>{rating.advice}</p>
        </div>
      )}

      {sitemap.last_submitted && (
        <p className="text-xs text-muted-foreground">
          Ingediend: {new Date(sitemap.last_submitted).toLocaleDateString('nl-BE')}
          {sitemap.last_downloaded &&
            ` · Gedownload: ${new Date(sitemap.last_downloaded).toLocaleDateString('nl-BE')}`}
        </p>
      )}
    </div>
  )
}

export function GscTools({ gscProperty }: { gscProperty: string | null }) {
  const [inspectInput, setInspectInput] = useState('')
  const { data: sitemaps, isLoading: sitemapsLoading } = useGscSitemaps(gscProperty ?? '')
  const inspect = useGscInspect()

  const handleInspect = async () => {
    if (!gscProperty || !inspectInput.trim()) return
    try {
      await inspect.mutateAsync({ siteUrl: gscProperty, inspectionUrl: inspectInput.trim() })
    } catch (err) {
      toast.error('URL inspectie mislukt', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
    }
  }

  if (!gscProperty) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sitemaps &amp; URL-inspectie</CardTitle>
          <CardDescription>
            Dit dossier heeft nog geen GSC-property. Stel er een in bij het dossier om
            sitemap-status en URL-inspectie te gebruiken.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle>Sitemaps</CardTitle>
              <CardDescription>Ingediend bij Google, met indexeringsgraad.</CardDescription>
            </div>
            <a
              href={`https://search.google.com/search-console?resource_id=${encodeURIComponent(gscProperty)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="h-3 w-3" /> GSC
            </a>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {sitemapsLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : !sitemaps?.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Geen sitemaps ingediend voor deze property.
            </p>
          ) : (
            sitemaps.map((s) => <SitemapCard key={s.path} sitemap={s} />)
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>URL-inspectie</CardTitle>
          <CardDescription>
            Live opvraging bij Google — los van het nachtelijke indexeerbaarheidsrapport.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="https://example.com/pagina"
              value={inspectInput}
              onChange={(e) => setInspectInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInspect()}
              className="flex-1"
            />
            <Button onClick={handleInspect} disabled={inspect.isPending || !inspectInput.trim()}>
              {inspect.isPending ? <Spinner size="sm" /> : 'Inspecteer'}
            </Button>
          </div>

          {inspect.data && (
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="truncate text-sm font-medium">{inspect.data.inspection_url}</span>
                <Badge
                  className={cn(
                    'shrink-0',
                    VERDICT_CLASSES[inspect.data.verdict] ?? 'bg-gray-400 text-white',
                  )}
                >
                  {VERDICT_LABELS[inspect.data.verdict] ?? inspect.data.verdict}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  { label: 'Dekkingsstatus', value: inspect.data.coverage_state },
                  { label: 'Indexeringsstatus', value: inspect.data.indexing_state },
                  { label: 'robots.txt', value: inspect.data.robots_txt_state },
                  { label: 'Pagina ophalen', value: inspect.data.page_fetch_state },
                  { label: 'Gecrawld als', value: inspect.data.crawled_as },
                  { label: 'Mobiel', value: inspect.data.mobile_verdict || '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded border p-2">
                    <div className="text-muted-foreground">{label}</div>
                    <div className="mt-0.5 truncate font-medium">{value || '—'}</div>
                  </div>
                ))}
              </div>
              {inspect.data.last_crawl_time && (
                <p className="text-xs text-muted-foreground">
                  Laatste crawl: {new Date(inspect.data.last_crawl_time).toLocaleString('nl-BE')}
                </p>
              )}
              {inspect.data.google_canonical &&
                inspect.data.google_canonical !== inspect.data.inspection_url && (
                  <p className="text-xs text-orange-600 dark:text-orange-300">
                    Google canonical: {inspect.data.google_canonical}
                  </p>
                )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
